import React, { useState, useEffect, useRef } from "react";
import { Box, Dialog, DialogTitle, DialogContent, IconButton, Typography, Container } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import DOMPurify from "dompurify";
import SlideContent from "./SlideContent";
import defaultIndexBg from "../../../assets/centrum_assets/design/index/firstBGS.png";
import defaultIndexBgMin from "../../../assets/centrum_assets/design/index/firstBGmin.png";

const CenteredSlide = ({ item, defaultBg, priority = false }) => {
    const [openModal, setOpenModal] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const descriptionRef = useRef(null);
    const mobileDescriptionRef = useRef(null);

    const desktopBg = item.image || defaultBg?.centered_bg || defaultIndexBg;
    const mobileBg = item.mobile_image || defaultBg?.centered_mobile || defaultIndexBgMin;

    // Custom Breakpoint Variables
    const MAX_MOBILE = "@media (max-width: 675px)";
    const MIN_DESKTOP = "@media (min-width: 676px)";

    useEffect(() => {
        const checkOverflow = () => {
            // Updated to check width against our custom breakpoint 675
            const el = window.innerWidth > 675 ? descriptionRef.current : mobileDescriptionRef.current;
            if (el) setIsOverflowing(el.scrollHeight > el.clientHeight);
        };
        checkOverflow();
        window.addEventListener("resize", checkOverflow);
        return () => window.removeEventListener("resize", checkOverflow);
    }, [item.description]);

    return (
        <Box sx={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}>
            
            {/* DESKTOP BACKGROUND - Hidden at 675px and below */}
            <Box 
                component="img" 
                src={desktopBg} 
                sx={{ 
                    width: "100%", height: "100%", objectFit: "cover", 
                    display: "none",
                    [MIN_DESKTOP]: { display: "block" }
                }} 
            />
            
            {/* MOBILE & TABLET LAYOUT - Flex stack triggers at 675px */}
            <Box sx={{ 
                display: "none", 
                flexDirection: "column", 
                height: "100%",
                [MAX_MOBILE]: { display: "flex" }
            }}>
                <Box 
                    component="img" 
                    src={mobileBg} 
                    sx={{ 
                        width: "100%", 
                        height: "45%", // Slightly taller for tablets
                        objectFit: "cover" 
                    }} 
                />
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", px: 4, py: 3, textAlign: "center", color: "#002349" }}>
                     <Container maxWidth="md">
                        <SlideContent 
                            item={item} 
                            isMobile={true} 
                            isCentered={true} 
                            align="center" 
                            descriptionRef={mobileDescriptionRef} 
                            isOverflowing={isOverflowing} 
                            onReadMore={() => setOpenModal(true)} 
                        />
                    </Container>
                </Box>
            </Box>

            {/* DESKTOP CONTENT - Centered Overlay, Hidden at 675px */}
            <Box sx={{
                position: "absolute", 
                top: "50%", 
                left: "50%", 
                transform: "translate(-50%, -50%)",
                textAlign: "center", 
                color: "#002349", 
                maxWidth: "1000px", 
                width: "90%", // Using percentage for better safety on smaller "desktop" widths
                zIndex: 10,
                display: "none",
                [MIN_DESKTOP]: { display: "block" }
            }}>
                <SlideContent 
                    item={item} 
                    isMobile={false} 
                    isCentered={true} 
                    align="center" 
                    descriptionRef={descriptionRef} 
                    isOverflowing={isOverflowing} 
                    onReadMore={() => setOpenModal(true)} 
                />
            </Box>

            {/* MODAL */}
            <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ textAlign: 'center', position: 'relative' }}>
                    <Typography 
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.title || "") }} 
                        sx={{ fontWeight: 800, fontSize: { xs: '1.5rem', md: '2rem' } }} 
                    />
                    <IconButton onClick={() => setOpenModal(false)} sx={{ position: 'absolute', right: 16, top: 16 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography component="div" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description || "") }} />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default CenteredSlide;